import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Flag, Trophy, Clock } from "lucide-react";
import ShareButton from "@/components/ShareButton";

const LOGO_GREEN = "https://customer-assets.emergentagent.com/job_ef6325d9-24bc-490f-8365-b7f21143b73f/artifacts/4pm3nkps_Modern%20logo%20for%20%27cadii%27%20with%20golf%20theme.-2.png";

const STAGE_STATUS = {
  draft: "Borrador", open: "Abierta", locked: "Cerrada",
  in_progress: "En Juego", completed: "Finalizada",
};

export default function PublicStagePage() {
  const { slug, rankingId, stageId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("leaderboard");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/public/clubs/${slug}/rankings/${rankingId}/stages/${stageId}`);
        if (res.ok) setData(await res.json());
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, [slug, rankingId, stageId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F5F4F0]"><div className="w-10 h-10 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F5F4F0]" style={{ fontFamily: "Outfit, sans-serif" }}>
      <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Etapa no encontrada</h1>
      <Link to={`/${slug}/rankings/${rankingId}`} className="text-[#1B4D3E] font-medium hover:underline">Volver al ranking</Link>
    </div>
  );

  const { club, stage, leaderboard, teetimes } = data;
  const hasLeaderboard = Object.keys(leaderboard).length > 0;
  const hasTeetimes = teetimes.length > 0;

  const LOGO_GREEN_LOCAL = "https://customer-assets.emergentagent.com/job_ef6325d9-24bc-490f-8365-b7f21143b73f/artifacts/4pm3nkps_Modern%20logo%20for%20%27cadii%27%20with%20golf%20theme.-2.png";
  const ogImage = club.logo_url || LOGO_GREEN_LOCAL;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareUrl = `${origin}/api/public/share/${slug}/rankings/${rankingId}/stages/${stageId}`;
  const ogTitle = `Etapa ${stage.stage_number}: ${stage.name} — ${club.name}`;
  const isLive = ["open", "locked", "in_progress"].includes(stage.status);
  const ogDescription = isLive
    ? `EN VIVO — ${STAGE_STATUS[stage.status] || stage.status} · ${stage.start_date || ""}`
    : `${STAGE_STATUS[stage.status] || stage.status} · ${stage.play_days} dia${stage.play_days > 1 ? "s" : ""}`;

  // Share text contextual: prioritize leaderboard over tee times
  const shareLabel = hasLeaderboard ? "Compartir resultados" : hasTeetimes ? "Compartir tee times" : "Compartir";

  return (
    <div className="min-h-screen bg-[#F5F4F0]" style={{ fontFamily: "Outfit, sans-serif" }}>
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/${slug}/rankings/${rankingId}`} className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">Volver al ranking</span>
          </Link>
          <Link to="/" className="opacity-60 hover:opacity-100"><img src={LOGO_GREEN} alt="cadii" className="h-6 rounded-lg" /></Link>
        </div>
      </header>

      {/* Stage Hero */}
      <section className="bg-[#1B4D3E] text-white">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-white/60 text-sm mb-1">{club.name}</p>
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="public-stage-title">
                Etapa {stage.stage_number}: {stage.name}
              </h1>
              <div className="flex flex-wrap gap-4 text-white/70 text-sm mt-2">
                {stage.start_date && <span>{stage.start_date}</span>}
                <span>{stage.play_days} dia{stage.play_days > 1 ? "s" : ""}</span>
                {stage.point_multiplier !== 1.0 && <span className="text-amber-300 font-medium">x{stage.point_multiplier} puntos</span>}
                <span className={isLive ? "text-red-300 font-semibold" : ""}>
                  {isLive && "• "}{STAGE_STATUS[stage.status] || stage.status}
                </span>
              </div>
            </div>
            <ShareButton
              title={ogTitle}
              text={ogDescription}
              url={shareUrl}
              variant="pill"
              label={shareLabel}
              testId="share-stage-btn"
            />
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Tabs */}
        {(hasLeaderboard || hasTeetimes) && (
          <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 w-fit" data-testid="stage-tabs">
            <button
              onClick={() => setTab("leaderboard")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "leaderboard" ? "bg-[#1B4D3E] text-white" : "text-[#64748B] hover:text-[#0F172A]"}`}
              data-testid="tab-leaderboard"
            >
              <Trophy className="w-4 h-4 inline mr-1.5" /> Resultados
            </button>
            {hasTeetimes && (
              <button
                onClick={() => setTab("teetimes")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === "teetimes" ? "bg-[#1B4D3E] text-white" : "text-[#64748B] hover:text-[#0F172A]"}`}
                data-testid="tab-teetimes"
              >
                <Clock className="w-4 h-4 inline mr-1.5" /> Tee Times
              </button>
            )}
          </div>
        )}

        {/* Leaderboard tab */}
        {tab === "leaderboard" && (
          hasLeaderboard ? (
            <div data-testid="public-stage-leaderboard">
              {Object.entries(leaderboard).map(([catName, players]) => (
                <Card key={catName} className="card-default mb-4">
                  <CardContent className="p-0">
                    <div className="bg-[#1B4D3E]/5 px-4 py-2.5 border-b border-gray-100">
                      <h3 className="font-bold text-sm text-[#1B4D3E]">{catName}</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-center px-3 py-2 text-xs font-semibold text-[#64748B] w-12">POS</th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-[#64748B]">JUGADOR</th>
                            {players[0]?.rounds?.map((_, i) => (
                              <th key={i} className="text-center px-3 py-2 text-xs font-semibold text-[#64748B]">R{i + 1}</th>
                            ))}
                            <th className="text-center px-3 py-2 text-xs font-semibold text-[#64748B]">TOTAL</th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-[#1B4D3E]">PTS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {players.map((p, idx) => (
                            <tr key={p.enrollment_id || p.member_id || idx} className={`border-b border-gray-50 ${idx < 3 ? "bg-amber-50/30" : ""}`}>
                              <td className="text-center px-3 py-2">
                                {(p.position || idx + 1) <= 3 ? (
                                  <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-xs font-bold text-white ${(p.position || idx + 1) === 1 ? "bg-amber-500" : (p.position || idx + 1) === 2 ? "bg-gray-400" : "bg-amber-700"}`}>{p.position || idx + 1}</span>
                                ) : (
                                  <span className="font-bold text-[#0F172A]">{p.position || idx + 1}</span>
                                )}
                              </td>
                              <td className="px-3 py-2 font-medium text-[#0F172A]">{p.player_name}</td>
                              {(p.rounds || []).map((r, i) => (
                                <td key={i} className="text-center px-3 py-2 text-[#64748B]">{r ?? "-"}</td>
                              ))}
                              <td className="text-center px-3 py-2 font-bold text-[#0F172A]">{p.total_score ?? "-"}</td>
                              <td className="text-center px-3 py-2 font-bold text-[#1B4D3E]">{p.points || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-default"><CardContent className="py-12 text-center"><p className="text-[#94A3B8]">Sin resultados disponibles</p></CardContent></Card>
          )
        )}

        {/* Tee Times tab */}
        {tab === "teetimes" && hasTeetimes && (
          <div data-testid="public-stage-teetimes">
            {teetimes.map((tt) => (
              <div key={tt.tee_time_id} className="mb-6">
                <h3 className="font-bold text-[#0F172A] mb-3">Dia {tt.day} — {tt.total_groups} grupos</h3>
                <div className="space-y-2">
                  {(tt.groups || []).map((g) => (
                    <Card key={g.group_number} className="card-default">
                      <CardContent className="p-3 flex items-start gap-4">
                        <div className="min-w-[60px] text-center">
                          <p className="text-xs text-[#94A3B8]">Grupo {g.group_number}</p>
                          <p className="font-bold text-[#0F172A] text-lg">{g.time}</p>
                          <p className="text-xs text-[#94A3B8]">Hoyo {g.hole}</p>
                        </div>
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {g.players.map((p) => (
                            <div key={p.member_id} className="flex items-center gap-2">
                              <span className="text-sm text-[#0F172A]">{p.name}</span>
                              {p.handicap_index != null && (
                                <span className="text-xs text-[#94A3B8]">
                                  ({p.handicap_index < 0 ? `+${Math.abs(p.handicap_index)}` : p.handicap_index})
                                </span>
                              )}
                              {p.category_name && (
                                <span className="text-xs bg-[#1B4D3E]/10 text-[#1B4D3E] px-1.5 py-0.5 rounded">{p.category_name}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <footer className="mt-12 py-8 px-6 text-center bg-white/60 border-t border-gray-100">
        <img src={LOGO_GREEN} alt="cadii" className="h-6 rounded-lg mx-auto opacity-60 mb-2" />
        <p className="text-xs text-[#9CA3AF]">&copy; {new Date().getFullYear()} cadii</p>
      </footer>
    </div>
  );
}
